import React, { useRef, useState } from 'react';
import { chain } from '../data/chainData';
import { archive } from '../data/archiveData';
import './Home.css';
import Archive from './Archive';

const number = (index) => String(index).padStart(3, '0');

function ExperienceBlock({ block, index, active, setRef, goTo, photoCount, onShowPhotos }) {
  const [sections, setSections] = useState([]);
  const toggle = (section) => {
    setSections((current) => current.includes(section)
      ? current.filter((item) => item !== section) : [...current, section]);
  };
  const TitleElement = block.cardHref ? 'a' : 'span';
  const linkedCard = Boolean(block.cardHref);
  const isHead = index === chain.length - 1;
  return (
    <article ref={setRef} className={`chain-block ${active ? 'is-current' : ''} ${isHead ? 'is-now' : ''} ${linkedCard ? 'has-detail' : ''}`}
      aria-labelledby={`title-${block.id}`}
      onKeyDown={(event) => { if (event.key === 'Escape') setSections([]); }}>
      <div className="chain-block-meta">
        <span>BLOCK #{number(index)}</span>
        {isHead ? (
          <span className="chain-status chain-building" aria-label="Inflight, building">
            <span className="chain-pixel-matrix" aria-hidden="true">
              {Array.from({ length: 16 }, (_, pixel) => (
                <span key={pixel} style={{ '--pixel-step': `${((pixel * 7) % 16) * 100}ms` }} />
              ))}
            </span>
            <span>INFLIGHT /<br />BUILDING</span>
          </span>
        ) : <span className="chain-status">{index === 0 ? 'GENESIS' : 'EXECUTION'}</span>}
      </div>
      <div className="chain-block-header">
        <span className="chain-section-label">Header</span>
        <dl>
          <div><dt>number</dt><dd>{number(index)}</dd></div>
          <div><dt>parent</dt><dd>{index === 0 ? <span className="chain-null">None / genesis</span> :
            <button onClick={() => goTo(index - 1)} aria-label={`Previous block: ${chain[index - 1].name}`}>
              <span aria-hidden="true">← </span>#{number(index - 1)} · {chain[index - 1].name}
            </button>}</dd></div>
        </dl>
      </div>
      <div className="chain-block-body">
        <span className="chain-section-label">Body / {block.theme}</span>
        <div className="chain-title-row">
          <h2 id={`title-${block.id}`} className={block.logo ? 'chain-logo-title' : undefined}>
            <TitleElement href={block.cardHref} target={block.cardHref ? '_blank' : undefined} rel={block.cardHref ? 'noreferrer' : undefined}
              className={linkedCard ? 'chain-project-link' : undefined} aria-label={linkedCard ? `Explore ${block.name}` : undefined}>
            {block.logo ? (block.logoStyle === 'dhow' ? (
              <span className="chain-brand chain-brand--dhow" role="img" aria-label="Dhow">
                <span className="chain-dhow-boat"><img src={block.logo} alt="" /></span>
                <span className="chain-dhow-wordmark"><img src={block.logo} alt="" /></span>
              </span>
            ) : block.logoStyle === 'knewit' ? (
              <span className="chain-brand chain-brand--knewit" role="img" aria-label="Knewit">
                <span className="chain-knewit-smile"><img src="/assets/brands/knewit-smile.png" alt="" /></span>
                <img className="chain-knewit-wordmark" src={block.logo} alt="" />
              </span>
            ) : <span className={`chain-brand chain-brand--${block.logoStyle || 'square'}`}>
              <img src={block.logo} alt={block.name} />
            </span>) : block.name}
            </TitleElement>
          </h2>
        </div>
        <p className="chain-block-summary">{block.summary}</p>
        <div className="chain-payload">
          {['transactions', 'state'].map((section) => {
            const expanded = sections.includes(section);
            return <div className="chain-disclosure" key={section}>
              <button className="chain-disclosure-trigger" aria-expanded={expanded}
                aria-label={`${section === 'transactions' ? 'Transactions' : 'State changes'} for ${block.name}`}
                aria-controls={`${block.id}-${section}`} onClick={() => toggle(section)}>
                <span>{section === 'transactions' ? 'Transactions' : 'State changes'}</span>
                <span aria-hidden="true">{expanded ? '−' : '+'}</span>
              </button>
              <div className="chain-disclosure-content" id={`${block.id}-${section}`} hidden={!expanded}>
                {section === 'transactions' ? <ul>{block.transactions.map((transaction) => <li key={transaction}>{transaction}</li>)}</ul>
                  : <p>{block.stateChange}</p>}
              </div>
            </div>;
          })}
        </div>
      </div>
      <div className="chain-block-bottom">
        <div className="chain-block-actions">
        {block.href ? <a href={block.href} target={block.href.startsWith('https:') ? '_blank' : undefined}
          rel={block.href.startsWith('https:') ? 'noreferrer' : undefined}>{block.linkLabel} <span aria-hidden="true">↗</span></a>
          : <span>{block.label}</span>}
        {photoCount > 0 && <button className="chain-photo-link" onClick={() => onShowPhotos(block.name)}
          aria-label={`View ${block.name} photos (${photoCount})`} aria-controls="project-archive">
          View photos <span aria-hidden="true">· {String(photoCount).padStart(2, '0')} ↘</span>
        </button>}
        </div>
        <span className="chain-end-label">{isHead ? 'TO BE CONTINUED' : `→ #${number(index + 1)}`}</span>
      </div>
    </article>
  );
}

export default function Home() {
  const railRef = useRef(null);
  const blocksRef = useRef([]);
  const [active, setActive] = useState(0);
  const [photoRequest, setPhotoRequest] = useState(null);

  const goTo = (index) => {
    const rail = railRef.current;
    const target = Math.max(0, Math.min(chain.length - 1, index));
    const block = blocksRef.current[target];
    if (!rail || !block) return;
    const inset = parseFloat(getComputedStyle(rail).paddingLeft);
    rail.scrollTo({
      left: block.offsetLeft - inset,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  const onScroll = () => {
    const rail = railRef.current;
    const inset = parseFloat(getComputedStyle(rail).paddingLeft);
    let closest = 0;
    let distance = Infinity;
    blocksRef.current.forEach((block, index) => {
      const next = Math.abs(block.offsetLeft - inset - rail.scrollLeft);
      if (next < distance) { distance = next; closest = index; }
    });
    setActive(closest);
  };

  const onKeyDown = (event) => {
    if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? chain.length - 1
      : Math.max(0, Math.min(chain.length - 1, active + (event.key === 'ArrowRight' ? 1 : -1)));
    goTo(next);
  };

  return (
    <main className="chain-page">
      <a className="chain-skip" href="#journey">Skip to the chain</a>
      <header className="chain-header">
        <a className="chain-wordmark" href="/" aria-label="Mark Chen home"><span className="chain-mark" aria-hidden="true" />Mark Chen</a>
        <nav aria-label="Contact links">
          <a href="https://github.com/MarkChen12138" target="_blank" rel="noreferrer">GitHub <span aria-hidden="true">↗</span></a>
          <a href="https://www.linkedin.com/in/jiale-chen-sqdi/" target="_blank" rel="noreferrer">LinkedIn <span aria-hidden="true">↗</span></a>
        </nav>
      </header>

      <section className="chain-intro" aria-labelledby="chain-title">
        <p className="chain-eyebrow">A personal chain / six chapters</p>
        <div className="chain-intro-row">
          <h1 id="chain-title"><span className="chain-title-name">mark chen<span className="chain-title-comma">，</span></span>{' '}<span className="chain-title-role">builder<span className="chain-title-jp" lang="ja">です</span></span></h1>
          <p className="chain-intro-note">One thing led to another.<br />{' '}This is the thread so far.</p>
        </div>
      </section>

      <section id="journey" className="chain-journey" aria-label="Mark’s journey">
        <div className="chain-rail" ref={railRef} onScroll={onScroll} onKeyDown={onKeyDown}
          tabIndex={0} aria-label="Six chapters. Scroll horizontally or use the left and right arrow keys.">
          {chain.map((block, index) => <ExperienceBlock key={block.id} block={block} index={index}
            active={active === index} goTo={goTo}
            photoCount={archive.filter((item) => item.project === block.name && item.src).length}
            onShowPhotos={(project) => setPhotoRequest({ project })}
            setRef={(element) => { blocksRef.current[index] = element; }} />)}
        </div>
      </section>

      <footer className="chain-footer">
        <div className="chain-navigation">
          <span className="chain-position" aria-live="polite">{number(active)} <span>/ 005</span></span>
          <nav className="chain-index" aria-label="Jump to a chapter">
            {chain.map((block, index) => <button key={block.id} onClick={() => goTo(index)}
              aria-label={`Go to ${block.name}`} aria-current={active === index ? 'step' : undefined}
              className={active === index ? 'is-active' : ''}><span>{block.name}</span></button>)}
          </nav>
          <div className="chain-arrows">
            <button onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous chapter">←</button>
            <button onClick={() => goTo(active + 1)} disabled={active === chain.length - 1} aria-label="Next chapter">→</button>
          </div>
        </div>
        <Archive photoRequest={photoRequest} />
        <div className="chain-colophon"><span>Independent paths. One continuous chain.</span><span className="chain-hint">Scroll to explore <span aria-hidden="true">↔</span></span><span>© {new Date().getFullYear()} Mark Chen</span></div>
      </footer>
    </main>
  );
}
